import { CurrentLocationMenu, MapPinIcon } from '@ahamove/icons/map';
// import { color } from '@ahamove/tokens';
import type { Address } from '@ahm/api-wrappers-core';
import { useLocationStore, useMapControl } from '@ahm/react-map/store';
import { toast } from '@ahm/react-ui/components/toast';
import { ActionIcon, Divider, Menu, Text, rem } from '@mantine/core';
import { LucideMoreVertical } from 'lucide-react';
import { memo, useCallback, useEffect, useState } from 'react';
import {
  createTranslations,
  locales as localizes,
  type Locales,
} from '../locales';
import type { BaseAddressAutocompleteProps } from '../types';
import { formatCoordinatesToLatLng } from '@/lib/utils';
// import withTooltip from '../with-tooltip';
// import useAutocompleteText from './use-autocomplete-text';

type ActionMenuAutocompleteProps = {
  handleSelectAddress: (address: Address) => void;
  lang?: keyof Locales;
  isPinLocation?: boolean;
  pathIndex?: number;
} & Pick<BaseAddressAutocompleteProps, 'pointType'>;

const ActionMenuAutocomplete: React.FC<ActionMenuAutocompleteProps> = memo(
  ({
    lang = 'vi',
    isPinLocation = false,
    handleSelectAddress,
    pointType,
    pathIndex,
  }) => {
    const t = createTranslations({ locales: localizes, lang });
    // const { currentLocationStatus } = useAutocompleteText({});
    const setPathFocusIndex = useLocationStore.use.setPathFocusIndex();

    const detectLocation = useLocationStore.use.detectLocation();
    // const loadingLocation = useLocationStore.use.loading();
    // const errorLocation = useLocationStore.use.error();
    const showPinMap = useMapControl.use.showPinMap();

    const [openMenu, setOpenMenu] = useState(false);

    useEffect(() => {
      detectLocation().catch((message) => {
        console.log('[location] detectLocation error', message);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMenuToggle = useCallback(() => {
      setOpenMenu((prev) => !prev);
    }, []);

    const handleSelectCurrentLocation = useCallback(() => {
      detectLocation()
        .then((response) => {
          setOpenMenu(false);
          const formattedResponse = formatCoordinatesToLatLng(response, [
            'entrances.coordinates',
            'entrances.coordinate',
          ]);
          handleSelectAddress(formattedResponse);
          toast.success({
            title: 'Xác nhận vị trí thành công.',
            withIcon: true,
            withCloseButton: false,
          });
        })
        .catch((error) => {
          if (error === 'User denied Geolocation') {
            toast.error({
              title:
                'Không thể xác định vị trí của bạn, vui lòng mở quyền truy cập vị trí trên trình duyệt.',
              withIcon: true,
              withCloseButton: false,
            });
          } else {
            console.error(error);
            toast.error({
              title: 'Không thể xác định vị trí của bạn, vui lòng thử lại.',
              description: error?.message,
              withIcon: true,
              withCloseButton: false,
            });
          }
        });
    }, [detectLocation, handleSelectAddress]);

    const handleSelectPinOnMap = useCallback(() => {
      setPathFocusIndex(pathIndex);
      showPinMap(pointType === 'pickup' ? 0 : 1);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pointType]);

    return (
      <Menu
        position="left-start"
        styles={{
          dropdown: {
            padding: rem(8),
            borderRadius: rem(8),
            border: 'none',
            boxShadow: '0px 16px 48px 0px rgba(0, 0, 0, 0.08)',
          },
          item: {
            padding: `${rem(10)} ${rem(12)}`,
          },
          itemLabel: {
            maxWidth: 180,
            overflowX: 'hidden',
          },
        }}
        withArrow
        closeOnItemClick={false}
        opened={openMenu}
        onChange={setOpenMenu}
        withinPortal={false}
      >
        <Menu.Target>
          <ActionIcon
            size="md"
            variant="default"
            color="neutral.7"
            styles={{ root: { borderColor: 'transparent' } }}
            onClick={handleMenuToggle}
          >
            <LucideMoreVertical size={20} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          {isPinLocation ? (
            <Menu.Item
              leftSection={<MapPinIcon width={24} height={24} />}
              onClick={handleSelectPinOnMap}
            >
              <Text fz={18} truncate fw={500}>
                {t('choose_from_map')}
              </Text>
            </Menu.Item>
          ) : null}

          <Divider my={4} mx={-8} />

          <Menu.Item
            title={t('use_current_location')}
            leftSection={<CurrentLocationMenu width={24} height={24} />}
            onClick={handleSelectCurrentLocation}
            // rightSection={
            //   loadingLocation ? (
            //     <ThemeIcon mt={-2} size={24} variant="transparent">
            //       <Loader size={18} color="primary" />
            //     </ThemeIcon>
            //   ) : errorLocation ? (
            //     <>
            //       {withTooltip(
            //         <ThemeIcon mt={-2} size={24} variant="transparent">
            //           <Info
            //             size={18}
            //             color={color.neutral['10']}
            //             fill={color.red['50']}
            //           />
            //         </ThemeIcon>,
            //         currentLocationStatus,
            //         true
            //       )}
            //     </>
            //   ) : null
            // }
          >
            <Text fz={18} truncate fw={500}>
              {t('use_current_location')}
            </Text>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  }
);

ActionMenuAutocomplete.displayName = 'ActionMenuAutocomplete';

export default ActionMenuAutocomplete;
